import type { SelfServiceSMBSignupStep } from '../types';

// Minimal viewer shape needed to derive the resume step. Mirrors the parts
// of `core/viewer.ts` Viewer that we read here. Kept narrow so the hook
// stays unit-testable without a full Apollo cache.
export interface ResumeViewerShape {
  id?: string | null;
  signup_type?: string | null;
  email?: { verified?: boolean | null } | null;
  groups?: ReadonlyArray<{
    id?: string | null;
    billing_account?: unknown | null;
  } | null> | null;
  pendingTeamMembership?: {
    id?: string | null;
    teamId?: string | null;
    teamName?: string | null;
  } | null;
}

export interface ResumeDecision {
  // null when the user is anonymous, fully signed up, or not in the SMB
  // self-service cohort. The caller should mount the modal at `account`
  // for an opt-in (anonymous) signup, or skip the modal entirely for
  // signed-up users.
  step: SelfServiceSMBSignupStep | null;
  // True when the user has a team with billing — caller should dismiss
  // the modal and route to dashboard.
  hasTeamBilling: boolean;
  // True when the user is anonymous (no viewer.id).
  anonymous: boolean;
}

function hasTeamBillingFromViewer(viewer: ResumeViewerShape | null | undefined): boolean {
  return !!viewer?.groups?.some((g) => !!g?.billing_account);
}

/**
 * Derive where the self-service SMB signup modal should resume for a given
 * viewer. Pure function — no hooks, no side effects. Backend is the single
 * source of truth (R-1).
 *
 * | Backend state                                        | Returned step    |
 * |------------------------------------------------------|------------------|
 * | no viewer                                            | null (anonymous) |
 * | viewer + signup_type !== 'smb'                       | null             |
 * | viewer + signup_type === 'smb' + team billing exists | null             |
 * | viewer + email unverified                            | 'email-otp'      |
 * | viewer + email verified + no team membership         | 'team-create'    |
 * | viewer + team membership + no team billing           | 'subscription'   |
 */
export function deriveResumeStep(viewer: ResumeViewerShape | null | undefined): ResumeDecision {
  if (!viewer || !viewer.id) {
    return { step: null, hasTeamBilling: false, anonymous: true };
  }

  const hasTeamBilling = hasTeamBillingFromViewer(viewer);
  if (hasTeamBilling) {
    return { step: null, hasTeamBilling: true, anonymous: false };
  }

  // R-6: SMB-only resume gate. Other no-billing users (legacy / expert /
  // partner-onboarding) are not auto-routed into self-service.
  if (viewer.signup_type !== 'smb') {
    return { step: null, hasTeamBilling: false, anonymous: false };
  }

  if (!viewer.email?.verified) {
    return { step: 'email-otp', hasTeamBilling: false, anonymous: false };
  }

  // Outstanding awaiting_approval join request: skip the picker and the
  // team-create form; render the "Request sent" status instead so the
  // user can't re-request or start a parallel self-service team.
  if (viewer.pendingTeamMembership) {
    return { step: 'team-request-sent', hasTeamBilling: false, anonymous: false };
  }

  // A user reaches this branch with a verified email and no team-level
  // billing. `groups` carries the workspace group created at team-create
  // time (companyName submission). Empty groups means we haven't run
  // team-create yet; non-empty means we have, and the missing piece is
  // billing.
  const hasTeamMembership = !!viewer.groups && viewer.groups.length > 0;
  if (!hasTeamMembership) {
    return { step: 'team-create', hasTeamBilling: false, anonymous: false };
  }

  return { step: 'subscription', hasTeamBilling: false, anonymous: false };
}

/**
 * The two null-step cases above need opposite handling (ENG-5769). An
 * anonymous visitor opts in at `account`. A signed-in viewer with nothing to
 * resume — fully signed up, or outside the SMB self-service cohort — must not
 * see the modal at all: at `account` it offers them an account they already
 * have, and createUser can only fail with "email already exists".
 */
export function isSignedInWithNothingToResume(decision: ResumeDecision): boolean {
  return decision.step === null && !decision.anonymous;
}
